/* eslint-disable class-methods-use-this */

/**
 * Describes manner by which a quantity of bytes will be formatted.
 */
enum ByteFormat {
    // Use Base 2 (1 KiB = 1024 bytes). Recommended for RAM size, size of files on disk.
    IEC = 1,
    // Use Base 10 (1 kB = 1000 bytes). Recommended for sizes of files on disk, disk sizes, bandwidth.
    SI = 0,
}
// eslint-disable-next-line padded-blocks
class FileUtil {

    /**
     * Returns a human-readable representation of a quantity of bytes in the most reasonable unit of magnitude.
     * @example
     * formatBytes(0) // returns "0 bytes"
     * formatBytes(1) // returns "1 byte"
     * formatBytes(1024, ByteFormat.IEC) // returns "1 KiB"
     * formatBytes(1024, ByteFormat.SI) // returns "1.02 kB"
     * @param size The size in bytes.
     * @param format Format using SI (Base 10) or IEC (Base 2). Defaults to SI.
     * @returns A string describing the bytes in the most reasonable unit of magnitude.
     */
    formatBytes = (value: number, format: ByteFormat = ByteFormat.SI): string => {
        const [multiple, k, suffix] = (format === ByteFormat.SI
            ? [1000, 'k', 'B']
            : [1024, 'K', 'iB']) as [number, string, string]
        const exp = value === 0 ? 0 : Math.floor(Math.log(value) / Math.log(multiple))
        // eslint-disable-next-line prefer-exponentiation-operator
        const size = Number((value / Math.pow(multiple, exp)).toFixed(2))
        // eslint-disable-next-line prefer-template
        return size + ' ' + (exp ? (k + 'MGTPEZY')[exp - 1] + suffix : 'byte' + (size > 1 || size < 1 ? 's' : ''))
    }

    isImage = (fileType: string): boolean => fileType.indexOf('image') === 0
}

// Minimum dimensions for thumbnails
const MIN_THUMBNAIL_WIDTH = 140
const MIN_THUMBNAIL_HEIGHT = 60


// Calculates appropriate dimensions for a thumbnail and maintains aspect ratio
const calculateDimensions = (thumbnail: { height?: number; width?: number } | undefined): { height: number; width: number } => {
    if (!thumbnail) {
        return {
            height: MIN_THUMBNAIL_HEIGHT,
            width: MIN_THUMBNAIL_WIDTH,
        }
    }

    const origHeight = Math.max(thumbnail.height ?? 0, MIN_THUMBNAIL_HEIGHT)
    const origWidth = Math.max(thumbnail.width ?? 0, MIN_THUMBNAIL_WIDTH)


    // If both dimensions are already above minimum, use original values
    if (origWidth >= MIN_THUMBNAIL_WIDTH && origHeight >= MIN_THUMBNAIL_HEIGHT) {
        return {
            height: origHeight,
            width: origWidth,
        }
    }

    // Calculate the aspect ratio
    const aspectRatio = origWidth / origHeight
    if (origWidth < MIN_THUMBNAIL_WIDTH) {
        const newHeight = Math.max(
            MIN_THUMBNAIL_HEIGHT,
            Math.round(MIN_THUMBNAIL_WIDTH / aspectRatio),
        )
        return {
            height: newHeight,
            width: MIN_THUMBNAIL_WIDTH,
        }
    }

    const newWidth = Math.max(
        MIN_THUMBNAIL_WIDTH,
        Math.round(MIN_THUMBNAIL_HEIGHT * aspectRatio),
    )
    return {
        height: MIN_THUMBNAIL_HEIGHT,
        width: newWidth,
    }
}

const fileUtil = new FileUtil()
export {
    fileUtil,
    MIN_THUMBNAIL_HEIGHT,
    MIN_THUMBNAIL_WIDTH,
    calculateDimensions,
}
